import { REFERRALS_ENABLED } from "@/lib/features";
import ReferralLanding from "./pages/ReferralLanding";
import { clearReferralCode, getReferralCode } from "@/lib/referrals";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import Auth from "./pages/Auth.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import DesignDetail from "./pages/DesignDetail.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import Waitlist from "./pages/Waitlist.tsx";
import Pricing from "./pages/Pricing.tsx";
import Feed from "./pages/Feed.tsx";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import Privacy from "./pages/Privacy.tsx";
import NotFound from "./pages/NotFound.tsx";
import RequireAuth from "./components/RequireAuth.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import { supabase } from "@/integrations/supabase/client";
import {
  initPostHog,
  identifyUser,
  setPersonProperties,
  resetPostHog,
  track,
} from "@/lib/posthog";
import { identifyBrevo, trackBrevo } from "@/lib/brevo";
import { identifySentryUser, clearSentryUser } from "@/lib/sentry";

const queryClient = new QueryClient();

const SAVED_ITEMS_KEY = "saved-furniture-items";

const hydrateUser = async (user: {
  id: string;
  email?: string | null;
  created_at?: string;
  full_name?: string | null;
}) => {
  // Identify immediately — before any async work — so events fired during
  // this session are associated with the user in PostHog. Delaying this
  // until after DB queries causes a race where clicks fire anonymously.
  identifyUser(user.id, {
    email: user.email ?? undefined,
    created_at: user.created_at,
    plan: "beta",
  });

  // Best-effort counters + Brevo identify. Never block on these.
  try {
    const [{ count }, { data: profile }] = await Promise.all([
      supabase
        .from("design_history")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("user_id", user.id)
        .maybeSingle(),
    ]);

    let savedCount = 0;
    try {
      const raw = localStorage.getItem(SAVED_ITEMS_KEY);
      if (raw) savedCount = (JSON.parse(raw) as unknown[]).length || 0;
    } catch {
      /* ignore */
    }

    setPersonProperties({
      total_generations: count ?? 0,
      total_saves: savedCount,
      activated: (count ?? 0) > 0,
    });

    if (user.email) {
      let firstName = profile?.first_name ?? null;
      let lastName = profile?.last_name ?? null;
      if (!firstName && user.full_name) {
        const parts = user.full_name.trim().split(/\s+/);
        firstName = parts[0] ?? null;
        if (!lastName && parts.length > 1) lastName = parts.slice(1).join(" ");
      }
      void identifyBrevo({ email: user.email, firstName, lastName, createdAt: user.created_at ?? null });
    }
  } catch (err) {
    console.warn("[posthog] failed to hydrate person counters:", err);
  }
};

const SIGNUP_TRACKED_KEY = "ph-signed-up-tracked";
const SIGNUP_RECENCY_MS = 5 * 60 * 1000; // 5 min window after auth.users.created_at

const maybeTrackSignup = (user: {
  id: string;
  created_at?: string;
  app_metadata?: { provider?: string } | null;
}) => {
  if (!user.created_at) return;
  const createdMs = Date.parse(user.created_at);
  if (!Number.isFinite(createdMs)) return;
  if (Date.now() - createdMs > SIGNUP_RECENCY_MS) return;

  // Per-browser dedupe — guards refreshes, multi-tab, and re-login within the window.
  try {
    const tracked = localStorage.getItem(SIGNUP_TRACKED_KEY);
    if (tracked === user.id) return;
  } catch {
    /* ignore */
  }

  const provider = user.app_metadata?.provider ?? "email";
  track("user_signed_up", {
    source: provider,
    // $set_once ensures the person property only sticks the first time, giving
    // a server-side dedupe signal across browsers in addition to the local one.
    $set_once: { signed_up_at: new Date().toISOString(), signup_source: provider },
  });

  // Brevo: fire the event. Contact upsert (SIGNUP_DATE etc.) happens
  // server-side via an AFTER INSERT trigger on profiles.
  void trackBrevo("user_signed_up", { source: provider });


  try {
    localStorage.setItem(SIGNUP_TRACKED_KEY, user.id);
  } catch {
    /* ignore */
  }
};

const PostHogBootstrap = () => {
  useEffect(() => {
    initPostHog();
    supabase.auth.getSession().then(({ data }) => {
      const u = data.session?.user;
      if (u) {
        const meta = (u.user_metadata ?? {}) as { full_name?: string; name?: string };
        hydrateUser({
          id: u.id,
          email: u.email,
          created_at: u.created_at,
          full_name: meta.full_name ?? meta.name ?? null,
        });
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Defer all work: calling supabase inside this callback deadlocks the
      // auth lock and leaves pages like /auth/callback hanging on getSession.
      setTimeout(() => handleAuthEvent(event, session), 0);
    });
    const handleAuthEvent = (
      event: string,
      session: Awaited<ReturnType<typeof supabase.auth.getSession>>["data"]["session"],
    ) => {
      if (event === "SIGNED_OUT") {
        try {
          localStorage.removeItem(SIGNUP_TRACKED_KEY);
        } catch {
          /* ignore */
        }
        resetPostHog();
        clearSentryUser();
      } else if (session?.user) {
        const meta = (session.user.user_metadata ?? {}) as {
          full_name?: string;
          name?: string;
        };
        hydrateUser({
          id: session.user.id,
          email: session.user.email,
          created_at: session.user.created_at,
          full_name: meta.full_name ?? meta.name ?? null,
        });
        identifySentryUser({ id: session.user.id, email: session.user.email });
        if (event === "SIGNED_IN") {
          const refCode = REFERRALS_ENABLED ? getReferralCode() : null;
          if (refCode) {
            supabase.functions
              .invoke<{ ok: boolean; reason?: string }>("referrals", { body: { action: "claim", code: refCode } })
              .then(({ data }) => {
                // Clear on any definitive answer; keep on network error to retry next sign-in.
                if (data) clearReferralCode();
                if (data?.ok) track("referral_claimed", { code: refCode });
              });
          }
          maybeTrackSignup({
            id: session.user.id,
            created_at: session.user.created_at,
            app_metadata: session.user.app_metadata as { provider?: string },
          });
        }
      }
    };
    return () => sub.subscription.unsubscribe();
  }, []);
  return null;
};

const App = () => (
  <Sentry.ErrorBoundary fallback={
    <div className="min-h-[100dvh] flex items-center justify-center text-center px-6">
      <div className="space-y-3">
        <p className="text-foreground font-semibold">Something went wrong.</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-muted-foreground underline"
        >
          Reload the page
        </button>
      </div>
    </div>
  }>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PostHogBootstrap />
        <Routes>
          <Route path="/" element={<Auth />} />
          <Route path="/r/:code" element={REFERRALS_ENABLED ? <ReferralLanding /> : <Navigate to="/" replace />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <Index />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/design/:id"
            element={
              <RequireAuth>
                <DesignDetail />
              </RequireAuth>
            }
          />
          <Route
            path="/feed"
            element={
              <RequireAuth>
                <Feed />
              </RequireAuth>
            }
          />
          <Route
            path="/pricing"
            element={
              <RequireAuth>
                <Pricing />
              </RequireAuth>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
